/**
 * Navigation Configuration
 *
 * Centralized navigation configuration for the journey-web application.
 * Defines sidebar navigation items, sections, and footer items.
 */

import {
  Archive,
  BadgeCheck,
  Briefcase,
  ClipboardCheck,
  CreditCard,
  GraduationCap,
  Heart,
  Home,
  Plane,
  Settings,
  User,
  Users,
} from '@tonnie/assets';

import {
  DEFAULT_SETTINGS_TAB,
  SETTINGS_PATH,
  settingsTabFromSearch,
  settingsTabHref,
} from '@/features/secondary/settings/settings.types';

import type { INavItem } from '../types/navigation.types';

// ═══════════════════════════════════════════════════════════════════════
// NAVIGATION ITEMS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Home navigation item
 */
export const homeNavItem: INavItem = {
  icon: Home,
  label: 'Home',
  href: '/dashboard',
};

/**
 * Hero navigation items - main journey heroes
 */
export const heroNavItems: INavItem[] = [
  { icon: ClipboardCheck, label: 'CheckUp', href: '/checkup', featureType: 'checkup' },
  { icon: Plane, label: 'TelePortal', href: '/teleportal', featureType: 'teleportal' },
  { icon: GraduationCap, label: 'QultureUp', href: '/qultureup', featureType: 'qultureup' },
  { icon: Heart, label: 'Care', href: '/care', featureType: 'care' },
  { icon: BadgeCheck, label: 'Passport', href: '/passport', featureType: 'passport' },
];

/**
 * Secondary navigation items - additional features
 */
export const secondaryNavItems: INavItem[] = [
  { icon: Users, label: 'Family', href: '/family' },
  { icon: Briefcase, label: 'Career', href: '/career' },
  // TestIt (BL-214) — citizen reusable-test catalog, above Profile.
  {
    icon: ClipboardCheck,
    label: 'TestIt',
    href: '/testit',
    translationKey: 'nav.testit',
  } as INavItem,
  { icon: User, label: 'Profile', href: settingsTabHref('profile'), translationKey: 'nav.profile' },
];

/**
 * Footer navigation items - utilities and settings
 */
export const footerNavItems: INavItem[] = [
  { icon: Archive, label: 'Vault', href: '/vault' },
  { icon: CreditCard, label: 'Billing', href: '/billing' },
  {
    icon: Settings,
    label: 'Settings',
    href: settingsTabHref('security'),
    translationKey: 'nav.settings',
  },
];

// ═══════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════

/** Whether `href` is the current path or one of its nested routes. */
function isRouteActive(currentPath: string, href: string): boolean {
  // Exact match
  if (currentPath === href) return true;

  // Dashboard special case
  if (currentPath === '/' && href === '/dashboard') return true;

  // Prefix match for nested routes (e.g., /checkup/assessment)
  if (href !== '/dashboard' && currentPath.startsWith(href + '/')) return true;

  return false;
}

/**
 * Whether a nav item is the current page, from the URL alone.
 *
 * Two items open Settings: Profile (its default tab) and Settings (the other
 * tabs). On /settings the tab in the URL decides — it used to be which item
 * was last CLICKED, so a link, a reload or a tab change lit the wrong one.
 */
export function isNavItemActive(currentPath: string, currentSearch: string, href: string): boolean {
  const [hrefPath = href, hrefSearch = ''] = href.split('?');
  if (hrefPath !== SETTINGS_PATH) return isRouteActive(currentPath, hrefPath);
  if (!isRouteActive(currentPath, SETTINGS_PATH)) return false;
  const onDefaultTab = settingsTabFromSearch(currentSearch) === DEFAULT_SETTINGS_TAB;
  const opensDefaultTab = settingsTabFromSearch(hrefSearch) === DEFAULT_SETTINGS_TAB;
  return onDefaultTab === opensDefaultTab;
}
